import { SendParams } from "./types";

type LogInvocationItem = (log: string, params: SendParams) => void;

type RateLimiterQueue = {
  invocationsCount: number;
  timer: NodeJS.Timeout | null;
  items: LogInvocationItem[];
};

type RateLimiterOptions = {
  maxInvocationsCount: number;
  burstWindowDurationInMS: number;
};

class RateLimiter {
  private maxInvocationsCount: number = 10; // 10 logs per second
  private burstWindowDurationInMS: number = 1000; // One Second
  private queuesByType: Record<string, RateLimiterQueue> = {};

  constructor(options: RateLimiterOptions) {
    if (options.maxInvocationsCount <= 0 || options.maxInvocationsCount > 25) {
      throw new Error("maxInvocationsCount should be between 1 and 25");
    }
    this.maxInvocationsCount = options.maxInvocationsCount;
    this.burstWindowDurationInMS = options.burstWindowDurationInMS;
  }

  getQueueByType(type: string) {
    return (
      this.queuesByType[type] ||
      (this.queuesByType[type] = { invocationsCount: 0, timer: null, items: [] })
    );
  }

  limitLogCall(log: string, params: SendParams, fn: LogInvocationItem) {
    const type = String(log);
    const queue = this.getQueueByType(type);

    // If we are over the rate limit, queue the log invocation
    if (queue.invocationsCount >= this.maxInvocationsCount) {
      if (!queue.timer) {
        // create a burst window that resets on burstWindowDurationInMS
        queue.timer = setTimeout(() => {
          queue.invocationsCount = 0;
          queue.timer = null;

          // If there are logs in the queue, process them
          while (queue.items.length > 0) {
            const queueItem = queue.items.shift();
            if (queueItem) {
              queueItem(log, params);
            }
          }
        }, this.burstWindowDurationInMS);
      }

      queue.items.push((log, params) => fn(log, params));
      return;
    }

    queue.invocationsCount++;
    fn(log, params);
  }
}

export { RateLimiter };
